import React, { useState } from 'react';
import { useUpgradeStore } from '../../stores/upgradeStore';
import { RequestForm } from './RequestForm';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { firestore } from '../../lib/firebase/config';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

export const UpgradeRequestCreator = () => {
  const [passportNumber, setPassportNumber] = useState('');
  const [selectedPilgrim, setSelectedPilgrim] = useState<any>(null);
  const [selectedHotel, setSelectedHotel] = useState('');
  const [targetRoomType, setTargetRoomType] = useState<'double' | 'triple' | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { createRequest } = useUpgradeStore();
  const navigate = useNavigate();

  const handleSearch = async () => {
    if (!passportNumber) return;

    setIsSearching(true);
    setErrorMessage(null);
    try {
      const normalizedPassport = passportNumber.trim();

      // Search for pilgrim by passport
      const pilgrimsRef = collection(firestore, 'Pilgrims');
      const q = query(pilgrimsRef, where('Passport_Number', '==', normalizedPassport));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setErrorMessage('No pilgrim found with this passport number');
        setSelectedPilgrim(null);
        setIsSearching(false);
        return;
      }

      const pilgrimDoc = snapshot.docs[0];
      const pilgrimData = pilgrimDoc.data();

      // Get pilgrim's package
      const packagesRef = collection(firestore, 'Packages');
      const packageQuery = query(packagesRef, where('name', '==', pilgrimData.package_nickName));
      const packageSnapshot = await getDocs(packageQuery);

      if (packageSnapshot.empty) {
        setErrorMessage('Package not found for this pilgrim');
        setSelectedPilgrim(null);
        setIsSearching(false);
        return;
      }

      const packageData = packageSnapshot.docs[0].data();

      // Find the linked guide and organizer
      const linksRef = collection(firestore, 'PilgrimGuideLinks');
      const linkQuery = query(
        linksRef, 
        where('pilgrimId', '==', pilgrimDoc.id),
        where('status', '==', 'approved')
      );
      const linkSnapshot = await getDocs(linkQuery);

      if (linkSnapshot.empty) {
        setErrorMessage('No approved guide link found for this pilgrim');
        setSelectedPilgrim(null);
        setIsSearching(false);
        return;
      }

      const linkData = linkSnapshot.docs[0].data();
      
      // Get guide details
      const guidesRef = collection(firestore, 'Guides');
      const guideDoc = await getDocs(query(guidesRef, where('__name__', '==', linkData.guideId)));
      let guideName = linkData.guideName; // Default fallback to the name in the link
      
      if (!guideDoc.empty) {
        guideName = guideDoc.docs[0].data().name;
      }

      // Get organizer details
      const organizersRef = collection(firestore, 'Organizers');
      const organizerDoc = await getDocs(query(organizersRef, where('__name__', '==', linkData.organizerId)));
      let organizerName = linkData.organizerName; // Default fallback to the name in the link
      
      if (!organizerDoc.empty) {
        organizerName = organizerDoc.docs[0].data().name;
      }

      // Check if pilgrim already has an upgrade request for any hotel
      const upgradeRequestsRef = collection(firestore, 'UpgradeRequests');
      const existingRequestsQuery = query(upgradeRequestsRef, where('pilgrimId', '==', pilgrimDoc.id));
      const existingRequestsSnapshot = await getDocs(existingRequestsQuery);
      
      if (!existingRequestsSnapshot.empty) {
        // Check each existing request for hotel conflicts
        let hasExistingRequestForHotels = [];
        existingRequestsSnapshot.forEach(doc => {
          const requestData = doc.data();
          hasExistingRequestForHotels.push(requestData.fromHotel);
        });
        
        if (hasExistingRequestForHotels.length > 0) {
          setErrorMessage(`This pilgrim already has upgrade requests for the following hotels: ${hasExistingRequestForHotels.join(', ')}`);
          // Still set the pilgrim data so they can see other eligible hotels
        }
      }

      // Create pilgrim object with all necessary data
      setSelectedPilgrim({
        id: pilgrimDoc.id,
        name: pilgrimData.Name,
        passportNumber: pilgrimData.Passport_Number,
        packageName: pilgrimData.package_nickName,
        package: packageData,
        main_makkah_roomType: pilgrimData.main_makkah_roomType,
        shifting_makkah_roomType: pilgrimData.shifting_makkah_roomType,
        madinah_roomType: pilgrimData.madinah_roomType,
        guideId: linkData.guideId,
        guideName: guideName,  // Use the retrieved guide name
        organizerId: linkData.organizerId,
        organizerName: organizerName,  // Use the retrieved organizer name
        pilgrimName: pilgrimData.Name
      });

    } catch (error) {
      console.error('Error searching for pilgrim:', error);
      setErrorMessage('Error searching for pilgrim');
      setSelectedPilgrim(null);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedPilgrim || !selectedHotel || !targetRoomType) {
      toast.error('All fields are required');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      // Determine which hotel field this is in the pilgrim's package
      const pkg = selectedPilgrim.package;
      
      // Find start/end dates for the selected hotel
      let startDate = '';
      let endDate = '';
      
      if (selectedHotel === pkg.mainMakkahHotel) {
        startDate = pkg.mainMakkahStartDate;
        endDate = pkg.mainMakkahEndDate;
      } else if (selectedHotel === pkg.shiftingMakkahHotel) {
        startDate = pkg.shiftingMakkahStartDate;
        endDate = pkg.shiftingMakkahEndDate;
      } else if (selectedHotel === pkg.madinahHotel) {
        startDate = pkg.madinahStartDate;
        endDate = pkg.madinahEndDate;
      }
      
      // Find the original room type for this hotel
      let fromRoomType: 'triple' | 'quadruple';
      
      if (selectedHotel === pkg.mainMakkahHotel) {
        fromRoomType = selectedPilgrim.main_makkah_roomType as 'triple' | 'quadruple';
      } else if (selectedHotel === pkg.shiftingMakkahHotel) {
        fromRoomType = selectedPilgrim.shifting_makkah_roomType as 'triple' | 'quadruple';
      } else {
        fromRoomType = selectedPilgrim.madinah_roomType as 'triple' | 'quadruple';
      }

      // Create the request
      await createRequest({
        pilgrimId: selectedPilgrim.id,
        pilgrimName: selectedPilgrim.pilgrimName,
        passportNumber: selectedPilgrim.passportNumber,
        guideId: selectedPilgrim.guideId,
        guideName: selectedPilgrim.guideName, // Correctly use the guide name
        organizerId: selectedPilgrim.organizerId,
        organizerName: selectedPilgrim.organizerName, // Correctly use the organizer name
        fromHotel: selectedHotel,
        toHotel: selectedHotel, // Same hotel for room upgrades
        fromRoomType,
        toRoomType: targetRoomType,
        startDate,
        endDate
      });

      toast.success('Room upgrade request submitted successfully');
      navigate('/room-upgrade');
    } catch (error: any) {
      console.error('Error submitting upgrade request:', error);
      setErrorMessage(error.message || 'Failed to submit upgrade request');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <RequestForm
      passportNumber={passportNumber}
      setPassportNumber={setPassportNumber}
      selectedPilgrim={selectedPilgrim}
      selectedHotel={selectedHotel}
      setSelectedHotel={setSelectedHotel}
      targetRoomType={targetRoomType}
      setTargetRoomType={setTargetRoomType}
      isSearching={isSearching}
      isSubmitting={isSubmitting}
      onSearch={handleSearch}
      onSubmit={handleSubmit}
      errorMessage={errorMessage}
    />
  );
};